/**
 * InventoryPage.jsx
 * Inventory runway report — shows current stock, sell rate, days of supply,
 * and highlights overstock/low stock/dead stock with colour-coded badges.
 */

import React, { useState } from 'react';
import {
  Card,
  DataTable,
  SkeletonBodyText,
  Banner,
  EmptyState,
  Text,
  BlockStack,
  Badge,
  Pagination,
  Select,
  InlineStack,
  TextField,
  Button,
} from '@shopify/polaris';

import { useReport } from '../hooks/useReport';
import { useExport } from '../hooks/useExport';
import { api } from '../api/api';
import {
  formatCurrency,
  formatNumber,
  formatRunway,
  formatRate,
  periodLabel,
  runwayStatus,
} from '../utils/formatters';

const ROWS_PER_PAGE = 50;
const LOW_STOCK_THRESHOLD_DAYS = 30;

const INVENTORY_CSV_COLUMNS = [
  { key: 'productTitle', label: 'Product' },
  { key: 'sku', label: 'SKU' },
  { key: 'productType', label: 'Product Type' },
  { key: 'inventoryQuantity', label: 'Inventory Qty' },
  { key: 'dailySellRate', label: 'Daily Sell Rate' },
  { key: 'runwayDays', label: 'Runway (days)' },
  { key: 'overstockUnits', label: 'Overstock Units' },
  { key: 'overstockValue', label: 'Overstock Value' },
];

const SORT_OPTIONS = [
  { label: 'Runway (shortest first)', value: 'runwayDays:asc' },
  { label: 'Runway (longest first)', value: 'runwayDays:desc' },
  { label: 'Overstock value (high → low)', value: 'overstockValue:desc' },
  { label: 'Inventory qty (high → low)', value: 'inventoryQuantity:desc' },
  { label: 'Revenue (high → low)', value: 'revenue:desc' },
];

const STATUS_FILTERS = [
  { label: 'All SKUs', value: 'all' },
  { label: 'Overstock only', value: 'overstock' },
  { label: `Low stock (< ${LOW_STOCK_THRESHOLD_DAYS}d)`, value: 'lowstock' },
  { label: 'Dead stock', value: 'dead' },
];

/**
 * InventoryPage — inventory runway report with overstock/low stock/dead stock badges.
 *
 * @param {{ period: string, overstockDays: number, filterBar: JSX.Element }} props
 */
export function InventoryPage({ period, overstockDays, filterBar }) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('runwayDays:asc');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [sortBy, sortDir] = sort.split(':');

  const csvFilename = `inventory-runway-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
  const { exporting, handleExport } = useExport(
    api.getInventory,
    { period, overstockDays, sortBy, sortDir, search },
    INVENTORY_CSV_COLUMNS,
    csvFilename,
  );

  const { data, loading, error } = useReport(api.getInventory, {
    period,
    overstockDays,
    page,
    limit: ROWS_PER_PAGE,
    sortBy,
    sortDir,
    search,
  });

  // Client-side status filter (server returns all; we filter in-browser for speed)
  const allRows = data?.data || [];
  const filteredRows = allRows.filter((row) => {
    if (statusFilter === 'overstock') return row.isOverstocked;
    if (statusFilter === 'lowstock') return row.runwayDays !== null && row.runwayDays < LOW_STOCK_THRESHOLD_DAYS;
    if (statusFilter === 'dead') return row.isDeadStock;
    return true;
  });

  /** Render a colour-coded runway badge for a row. */
  function runwayBadge(row) {
    const status = runwayStatus(row.runwayDays, overstockDays);
    const label = formatRunway(row.runwayDays);
    return <Badge tone={status}>{label}</Badge>;
  }

  const tableRows = filteredRows.map((row) => [
    <BlockStack gap="050">
      <Text variant="bodyMd" fontWeight="semibold" as="span">{row.productTitle}</Text>
      <Text variant="bodySm" tone="subdued" as="span">{row.sku} · {row.variantTitle}</Text>
    </BlockStack>,
    row.productType,
    <Text fontWeight="semibold" as="span">{formatNumber(row.inventoryQuantity)}</Text>,
    formatRate(row.dailySellRate),
    runwayBadge(row),
    row.overstockValue > 0 ? (
      <Text tone="caution" as="span">{formatCurrency(row.overstockValue)}</Text>
    ) : '—',
    row.overstockUnits > 0 ? (
      <Badge tone="attention">{formatNumber(row.overstockUnits)} excess</Badge>
    ) : '—',
  ]);

  const hasData = !loading && !error;
  const isEmpty = hasData && filteredRows.length === 0;

  return (
    <BlockStack gap="400">
      <Card>{filterBar}</Card>

      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd" as="h2">
              Inventory Runway — {periodLabel(period)}
            </Text>
            <InlineStack gap="300" blockAlign="center">
              <Text variant="bodySm" tone="subdued" as="span">
                Overstock threshold: {overstockDays} days
              </Text>
              <Button
                size="slim"
                onClick={handleExport}
                loading={exporting}
                disabled={loading || !data?.data?.length}
              >
                Export CSV
              </Button>
            </InlineStack>
          </InlineStack>

          {/* Filters row */}
          <InlineStack gap="300" wrap>
            <div style={{ flex: 1, minWidth: 220 }}>
              <TextField
                label="" labelHidden
                placeholder="Search product or SKU…"
                value={search}
                onChange={(v) => { setSearch(v); setPage(1); }}
                clearButton
                onClearButtonClick={() => { setSearch(''); setPage(1); }}
                autoComplete="off"
              />
            </div>
            <div style={{ minWidth: 200 }}>
              <Select
                label="" labelHidden
                options={SORT_OPTIONS}
                value={sort}
                onChange={(v) => { setSort(v); setPage(1); }}
              />
            </div>
          </InlineStack>

          {/* Status quick-filters */}
          <InlineStack gap="200">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 20,
                  border: '1px solid',
                  borderColor: statusFilter === f.value ? '#008060' : '#C9CCCF',
                  background: statusFilter === f.value ? '#008060' : 'transparent',
                  color: statusFilter === f.value ? '#fff' : '#637381',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: statusFilter === f.value ? 600 : 400,
                }}
              >
                {f.label}
              </button>
            ))}
          </InlineStack>

          {error && (
            <Banner title="Error loading inventory" tone="critical"><p>{error}</p></Banner>
          )}

          {loading && <SkeletonBodyText lines={12} />}

          {isEmpty && (
            <EmptyState
              heading="No inventory data found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>No products match the current filters. Try adjusting your search or status filter.</p>
            </EmptyState>
          )}

          {hasData && filteredRows.length > 0 && (
            <>
              <DataTable
                columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'numeric', 'text']}
                headings={['Product / SKU', 'Type', 'In Stock', 'Sell Rate', 'Runway', 'Overstock $', 'Excess Units']}
                rows={tableRows}
              />
              <Pagination
                hasPrevious={page > 1}
                hasNext={page < (data?.pagination?.totalPages || 1)}
                onPrevious={() => setPage((p) => p - 1)}
                onNext={() => setPage((p) => p + 1)}
                label={`${filteredRows.length} SKUs shown`}
              />
            </>
          )}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
